import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useAuth } from "../components/contexts/AuthContext.jsx";
import { pluralize } from "../Pages/Plan/billingFormat.js";
import {
  BILLING_HOURS_QUERY_KEY,
  BILLING_QUERY_KEY,
  parseCheckoutReturn,
  syncBilling,
} from "../services/billingService.js";
import { FEATURE_HOUR_PACKAGE, PLANS_ON_SALE, hasAgencyFeature } from "../utils/agencyFeatures.js";
import { TICKET_HOURS_STATUS_QUERY_KEY } from "../services/ticketHoursService.js";
import { forgetStripeReturn, pollUntil, storedHoursTarget } from "./stripeReturn.js";

/** Return from an hours Checkout: syncs with Stripe until the balance reaches the expected one, an error or the deadline. */
export function useHoursCheckoutReturn({ enabled = true, notifyCancel = false, onDone } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // Sin planes a la venta, o en SERSEO (con bono), `sync/` devuelve directamente el saldo de horas
  const syncReturnsHours = !PLANS_ON_SALE || hasAgencyFeature(user, FEATURE_HOUR_PACKAGE);
  const checkout = enabled ? parseCheckoutReturn(searchParams) : null;
  const isHoursSuccess = checkout?.status === "success" && checkout.kind === "hours";
  const handledRef = useRef(false);

  const finish = () => {
    queryClient.invalidateQueries({ queryKey: TICKET_HOURS_STATUS_QUERY_KEY });
    onDone?.();
  };

  useEffect(() => {
    if (!checkout || handledRef.current) return;
    if (!isHoursSuccess && !(notifyCancel && checkout.status === "cancel")) return;
    handledRef.current = true;
    const expected = isHoursSuccess ? storedHoursTarget(checkout.hours) : null;
    forgetStripeReturn();
    setSearchParams({}, { replace: true });
    if (!isHoursSuccess) {
      toast.info("Has cancelado el pago. No se ha realizado ningún cargo.");
      return;
    }
    const hoursLabel = pluralize(checkout.hours, "hora", "horas");
    toast.info(`Pago completado. Estamos sumando ${hoursLabel} a tu saldo…`);
    pollUntil({
      tick: async () => {
        const synced = await syncBilling();
        if (!syncReturnsHours) queryClient.setQueryData(BILLING_QUERY_KEY, synced);
        const hours = syncReturnsHours ? synced : synced.hours;
        queryClient.setQueryData(BILLING_HOURS_QUERY_KEY, hours);
        return hours;
      },
      // Without the saved balance (another tab, cleared storage) one sync is all we can check
      isDone: (summary) => expected == null || summary.remaining_minutes >= expected,
    })
      .then(({ status }) => {
        if (status === "done") toast.success(`¡Listo! Ya tienes ${hoursLabel} más en tu saldo.`);
        else toast.info("El pago se ha completado, pero las horas aún no aparecen en tu saldo. Se sumarán en unos minutos.");
      })
      .catch(() => toast.error("No hemos podido comprobar el pago. Si se ha completado, las horas se sumarán en unos minutos."))
      .finally(finish);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout?.status, checkout?.kind]);
}
